// src/pages/Employees/components/GratuityEstimatorCard.jsx
import React, { useEffect, useState } from "react";
import { Card, Spin, Typography, Button } from "antd";
import { CalculatorOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { calculateFinalSettlementApi } from "../../../apiservice/hrpayroll";
import GratuityCalculatorModal from "./GratuityCalculatorModal";

const { Text } = Typography;

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "AED",
    minimumFractionDigits: 2,
  }).format(Number(amount || 0));

// Reuses the same calculate-only Final Settlement endpoint with
// last_working_day = today — an estimate for a still-active employee, not
// a real exit. The real, official calculation happens in the Final
// Settlement panel once the employee actually leaves.
const GratuityEstimatorCard = ({ employee, currency, isIndia }) => {
  const [gratuity, setGratuity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const allowanceMap = Array.isArray(employee?.allowance_values)
    ? employee.allowance_values.map((a) => ({
        name: (a.name || "").toLowerCase(),
        amount: parseFloat(a.amount || 0),
      }))
    : [];
  const basicSalary = allowanceMap.find((a) => a.name.includes("basic"))?.amount || 0;

  useEffect(() => {
    if (!employee?.employee_id || !employee?.joining_date || !basicSalary) {
      setGratuity(null);
      return;
    }
    setLoading(true);
    calculateFinalSettlementApi({
      employee_id: employee.employee_id,
      last_working_day: dayjs().format("YYYY-MM-DD"),
      basic_salary: basicSalary,
      total_salary: basicSalary,
    })
      .then((res) => setGratuity(res.data?.gratuity || null))
      .catch(() => setGratuity(null))
      .finally(() => setLoading(false));
  }, [employee?.employee_id, employee?.joining_date, basicSalary]);

  if (!employee?.employee_id) return null;

  return (
    <Card
      size="small"
      title="End of Service Gratuity (Estimate)"
      extra={
        <Button size="small" icon={<CalculatorOutlined />} onClick={() => setCalculatorOpen(true)}>
          Manual Calculator
        </Button>
      }
      style={{ marginBottom: 16, borderTop: "3px solid #722ed1" }}
    >
      {loading ? (
        <Spin size="small" />
      ) : !basicSalary ? (
        <Text type="secondary">Add a Basic Salary component to see an estimate.</Text>
      ) : !gratuity ? (
        <Text type="secondary">Unable to compute an estimate right now.</Text>
      ) : gratuity.eligible ? (
        <>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#237804" }}>
            {formatCurrency(gratuity.amount, currency)}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Based on {gratuity.completedYears} completed year(s) of service, if today were the last working day.
          </Text>
        </>
      ) : (
        <Text type="secondary">
          Not yet eligible
          {gratuity.yearsUntilEligible != null && ` — ${gratuity.yearsUntilEligible} more year(s) of service required`}.
        </Text>
      )}
      <div style={{ marginTop: 8, fontSize: 11, color: "#999" }}>
        Estimate only — the actual amount is calculated at exit via the Final Settlement panel.
      </div>

      <GratuityCalculatorModal
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        isIndia={isIndia}
        currency={currency}
      />
    </Card>
  );
};

export default GratuityEstimatorCard;
